import { DestroyRef, Directive, HostBinding, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DecisionSource } from '@bpos/v1/over-ask';
import { OverAskConfig } from '@frontend/sports/common/client-config-data-access';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { IUserBettingSettings, SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { ArcRestrictionService } from '../../../../../lib/arc/arc-restriction.service';
import { FeatureLocation, IARCProfileConfiguration } from '../../../../../lib/arc/arc.model';
import { IBetslipRootState } from '../../../../base/store/state';
import { IFailedSlipResult, ISucceededSlipResult } from '../../../betplacement/models';
import { isFailedSlip, isSucceededNonPartialSlip } from '../../../betplacement/utils';
import { betslipPlacedResultStateSelector } from '../../selectors';
import { BetslipResultConfigurationBase } from '../../services/result-configuration.service';
import { IBetslipResultState } from '../../state';

@Directive()
export default abstract class BaseResultComponent implements OnInit {
    @Input() state: IBetslipResultState;
    @Input() inColumn: boolean;
    @Input() oddsDisplayFormat: OddsDisplayFormat;

    @HostBinding('class') className = 'betslip-result';

    isPlatformError: boolean;
    platformErrorMessage = '';
    profileDetails$: Observable<IARCProfileConfiguration>;
    hasArcPostBetMessage$: Observable<boolean>;
    FeatureLocation = FeatureLocation;
    showStakeChangeBanner = false;
    showStakeSettingMsg = false;
    settings: IUserBettingSettings;
    setting?: number;
    private readonly destroyRef = inject(DestroyRef);

    protected constructor(
        arcRestrictionService: ArcRestrictionService,
        public resultConfiguration: BetslipResultConfigurationBase,
        public store: Store<IBetslipRootState>,
        public userService: UserService,
        private overAskConfig: OverAskConfig,
        private userSettings: SyncUserSettingsService,
    ) {
        this.profileDetails$ = arcRestrictionService.profileDetails;
        this.hasArcPostBetMessage$ = arcRestrictionService.postBetslipMessageEnabled$;
    }

    ngOnInit() {
        this.settings = Object.assign({ stakeChangeConsent: 0 }, this.userSettings.bettingSettings);
        this.setting = this.settings.stakeChangeConsent;
        this.resultConfiguration.onKeepPlacedBetsInBetslip(false);

        this.showStakeChangeBanner =
            this.state.decisionSource === DecisionSource.Auto &&
            this.state.isOfferModified &&
            this.setting === 0 &&
            this.overAskConfig.showStakeChanges;

        this.showStakeSettingMsg = this.state.decisionSource === DecisionSource.Auto && this.state.isOfferModified && this.setting === 1;

        this.store
            .select(betslipPlacedResultStateSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                result.slips.filter(isFailedSlip).map((slip) => {
                    const platformError = slip.errors.find((err) => err.platformError);
                    if (platformError?.platformError.range === 'platform') {
                        this.isPlatformError = true;
                        this.platformErrorMessage = platformError.errorMessage;
                    }
                });
            });
    }

    get succeededSlips(): ISucceededSlipResult[] {
        return this.state.result.slips.filter(isSucceededNonPartialSlip);
    }

    get failedSlips(): IFailedSlipResult[] {
        return this.state.result.slips.filter(isFailedSlip);
    }

    get isTeaserBet(): boolean {
        const pick = (this.state.result.slips[0] as ISucceededSlipResult)?.picks?.[0];

        return pick?.isTeaserBet!;
    }

    get userPoints(): number | undefined {
        return this.state.result.rewardPoints?.userPoints;
    }
}
